import { router } from '@inertiajs/react'
import { PlayCircleIcon, EyeIcon, ThumbsUpIcon, MessageSquareIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface Video {
  id: number
  video_id: string
  title: string
  thumbnail_url: string
  view_count: number
  like_count: number
  comment_count: number
  published_at: string
  duration: string | null
}

interface Connection {
  id: number
  channel_title: string
  subscriber_count: number
  video_count: number
}

interface Pagination {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface VideosIndexProps {
  videos: Video[]
  connection: Connection | null
  pagination: Pagination
}

export default function VideosIndex({ videos, connection, pagination }: VideosIndexProps) {
  const handleVideoClick = (videoId: number) => {
    router.visit(`/youtube/videos/${videoId}`)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`
    }
    return count.toLocaleString()
  }

  if (!connection) {
    return (
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center space-y-4">
                  <PlayCircleIcon className="size-16 text-red-600 mx-auto" />
                  <h2 className="text-2xl font-bold text-gray-900">No YouTube Connection</h2>
                  <p className="text-gray-600 max-w-md">
                    Connect your YouTube channel in the Integrations page to view your videos and manage comments.
                  </p>
                  <Button
                    onClick={() => router.visit('/admin/integrations')}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Go to Integrations
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            {/* Header */}
            <div className="flex flex-col gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">YouTube Videos</h1>
                <p className="text-muted-foreground">
                  View and manage comments on your YouTube channel
                </p>
              </div>

              {/* Channel Stats */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold">{connection.channel_title}</h2>
                      <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                        <span>{connection.subscriber_count.toLocaleString()} subscribers</span>
                        <span>•</span>
                        <span>{connection.video_count.toLocaleString()} videos</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Videos Table */}
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlayCircleIcon className="size-5" />
                  Videos ({videos.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="min-w-0">
                {videos.length === 0 ? (
                  <div className="text-center py-12">
                    <PlayCircleIcon className="size-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No videos found</p>
                    <p className="text-sm text-gray-500 mt-2">Videos are syncing from your channel...</p>
                  </div>
                ) : (
                  <div className="min-w-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[60px]">Thumbnail</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead className="w-[120px]">Published</TableHead>
                          <TableHead className="w-[100px] text-right">Views</TableHead>
                          <TableHead className="w-[100px] text-right">Likes</TableHead>
                          <TableHead className="w-[100px] text-right">Comments</TableHead>
                          <TableHead className="w-[100px]">Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {videos.map((video) => (
                          <TableRow
                            key={video.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleVideoClick(video.id)}
                            data-testid={`video-row-${video.id}`}
                          >
                            <TableCell>
                              <div className="relative w-[60px] h-[40px] bg-gray-100 rounded overflow-hidden">
                                <img
                                  src={video.thumbnail_url}
                                  alt={video.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="line-clamp-2 max-w-[400px]">
                                <a
                                  href={`https://www.youtube.com/watch?v=${video.video_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`video-title-link-${video.id}`}
                                >
                                  {video.title}
                                </a>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(video.published_at)}
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              <div className="flex items-center justify-end gap-1">
                                <EyeIcon className="size-3 text-muted-foreground" />
                                <span>{formatCount(video.view_count)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              <div className="flex items-center justify-end gap-1">
                                <ThumbsUpIcon className="size-3 text-muted-foreground" />
                                <span>{formatCount(video.like_count)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-sm">
                              <div className="flex items-center justify-end gap-1">
                                <MessageSquareIcon className="size-3 text-muted-foreground" />
                                <span>{formatCount(video.comment_count)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {video.duration || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                  <div className="mt-6 flex items-center justify-between border-t pt-4">
                    <div className="text-sm text-muted-foreground">
                      Showing {pagination.from} to {pagination.to} of {pagination.count} videos
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.prev === null}
                        onClick={() => router.visit(`/youtube/videos?page=${pagination.page - 1}`)}
                      >
                        Previous
                      </Button>
                      <div className="flex items-center px-3 text-sm">
                        Page {pagination.page} of {pagination.pages}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.next === null}
                        onClick={() => router.visit(`/youtube/videos?page=${pagination.page + 1}`)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
